import {
  IconAlertTriangle,
  IconBolt,
  IconBriefcase,
  IconBuildingCommunity,
  IconCalendar,
  IconChartPie,
  IconCheckbox,
  IconClipboardText,
  IconClockEdit,
  IconClockHour3,
  IconDoor,
  IconExclamationCircle,
  IconFileCheck,
  IconFileText,
  IconFolder,
  IconHomeSearch,
  IconId,
  IconLayoutBoard,
  IconLifebuoy,
  IconListCheck,
  IconMap2,
  IconMessageCircle,
  IconNews,
  IconParkingCircle,
  IconPencil,
  IconPhoto,
  IconRegistered,
  IconReportSearch,
  IconShieldCheck,
  IconSmartHome,
  IconSpeakerphone,
  IconSubtask,
  IconTemplate,
  IconUsers,
  IconWorld,
} from '@tabler/icons-react'

import { FilterObjectType } from '@/context/filterContext'
import { setFolderIdQueryParam } from '@/hooks/useDocumentFolders'
import { BASIC_ADMIN_ROLES, Role } from '@/modules/adminUsers/roles'
import { SubbieTabs } from '@/modules/subcontractors/constants'
import { TOOLBOX_TABS } from '@/modules/toolboxes/constants'
import { WorkerTabs } from '@/modules/workers/constants'
import { buildQueryStringList } from '@/utils/buildQueryStringList'
import { paramify, updateFiltersIfPresent } from '@/utils/navigationConfig'
import { Params, stringifyUrl } from '@/utils/params'

import config from 'src/config'

import { BuildPassModule } from 'types/api.graphql'
import { QuerySupport, RouteViewOptions } from 'types/navigation'

export const FAVOURITE_MODULES = {
  id: 'favourites',
  title: 'common:navigation.favouritesTitle',
  items: [],
}

export const CHECKLIST_ITEMS = [
  {
    id: 'checklistsChecklists' as const,
    querySupport: QuerySupport.BUILDER_AND_PROJECT,
    title: 'View all',
    path: '/checklists',
    actions: {
      newChecklist: (params?: Record<string, unknown>) =>
        `/checklists/new${params ? buildQueryStringList(params) : ''}`,
      newChecklistRegisterItem: (templateId: string) =>
        `/checklists/new/${templateId}/register-item`,
      newScheduledJob: (scheduledJobId: string) =>
        `/checklists/checklists/new/${scheduledJobId}`,
      share: (params?: Record<string, unknown>) =>
        `/checklists/share${params ? buildQueryStringList(params) : ''}`,

      checklistTypesList: () => '/checklists',
      checklistsOfTemplateList: (templateId: string) =>
        `/checklists/${templateId}`,

      viewChecklist: (templateId: string, templateResponseId: string) =>
        `/checklists/${templateId}/${templateResponseId}`,
      viewReport: (templateId: string, templateResponseId: string) => {
        return `/checklists/${templateId}/${templateResponseId}/report`
      },
      viewCategory: (categoryId: string) =>
        `/checklists/category/${categoryId}`,
      editPermissions: (templateId: string, templateResponseId: string) =>
        `/checklists/${templateId}/${templateResponseId}/permissions`,
    } as const,
    requiredRoles: BASIC_ADMIN_ROLES,
  },
  {
    id: 'checklistsTemplates' as const,
    querySupport: QuerySupport.BUILDER_AND_PROJECT,
    title: 'Templates',
    path: '/checklists/templates',
    requiredRoles: [Role.BuilderManager, Role.BuilderOwner, Role.SuperAdmin],
    actions: {
      list: (options?: FilterObjectType) =>
        updateFiltersIfPresent('/checklists/templates', options),
      view: (templateId: string) => `/checklists/templates/${templateId}`,
    } as const,
  },
  {
    id: 'checklistsSchedules' as const,
    title: 'Reminders',
    path: '/checklists/schedules',
    querySupport: QuerySupport.BUILDER_AND_PROJECT,
    actions: {
      new: () => '/checklists/schedules/new',
      list: (options?: FilterObjectType) =>
        updateFiltersIfPresent('/checklists/schedules', options),
      view: (scheduleId: string) => `/checklists/schedules/${scheduleId}`,
    } as const,
    requiredRoles: BASIC_ADMIN_ROLES,
  },
  {
    id: 'checklistAccessTemplates' as const,
    title: 'Access templates',
    path: '/checklists/access-templates',
    buildPassModule: BuildPassModule.AccessProfiles,
    querySupport: QuerySupport.BUILDER_AND_PROJECT,
    requiredRoles: [
      Role.BuilderManager,
      Role.BuilderOwner,
      Role.SuperAdmin,
      Role.BpSuperAdmin,
    ],
    actionsOnly: true,
    actions: {
      new: () => '/checklists/access-templates/new',
      list: (options?: FilterObjectType) =>
        updateFiltersIfPresent('/checklists/access-templates', options),
      edit: (accessTemplateId: string) =>
        `/checklists/access-templates/${accessTemplateId}/edit`,
    } as const,
  },
]

const GENERAL_MODULES = {
  id: 'general' as const,
  title: 'General',
  items: [
    {
      id: 'home' as const,
      buildPassModule: BuildPassModule.ProjectOverview,
      path: '/dashboards/overview',
      title: 'Home',
      icon: IconSmartHome,
      querySupport: QuerySupport.PROJECT_ONLY,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'adminReports' as const,
      buildPassModule: BuildPassModule.Reports,
      title: 'Reporting',
      icon: IconChartPie,
      requiredRoles: BASIC_ADMIN_ROLES,
      items: [
        {
          id: 'myDashboards' as const,
          buildPassModule: BuildPassModule.MyDashboards,
          path: '/dashboards',
          title: 'Dashboards',
          querySupport: QuerySupport.BUILDER_AND_PROJECT,
          actions: {
            list: () => `/dashboards`,
            new: () => `/dashboards/new`,
            view: (dashboardId: string, editMode?: boolean) =>
              `/dashboards/${dashboardId}${
                editMode === true
                  ? buildQueryStringList({
                      editMode,
                    })
                  : ''
              }`,
            edit: (dashboardId: string, returnUrl?: string) =>
              `/dashboards/${dashboardId}/edit${
                returnUrl
                  ? buildQueryStringList({
                      returnUrl,
                    })
                  : ''
              }`,
          } as const,
          requiredRoles: BASIC_ADMIN_ROLES,
        },
        {
          id: 'adminReportsUsage' as const,
          path: '/admin/reports/usage',
          title: 'Usage',
          querySupport: QuerySupport.BUILDER_AND_PROJECT,
        },
        {
          id: 'adminReportsPerformance' as const,
          path: '/admin/reports/performance',
          title: 'Performance',
          buildPassModule: BuildPassModule.PerformanceReport,
          querySupport: QuerySupport.BUILDER_AND_PROJECT,
          requiredRoles: [
            Role.BuilderOwner,
            Role.BuilderManager,
            Role.SuperAdmin,
          ],
        },
        {
          id: 'adminReportsSubcontractors' as const,
          buildPassModule: BuildPassModule.SubcontractorReport,
          path: '/admin/reports/subcontractors',
          title: 'Companies',
          querySupport: QuerySupport.BUILDER_AND_PROJECT,
        },
        {
          id: 'adminReportsDefects' as const,
          buildPassModule: BuildPassModule.DefectReport,
          path: '/admin/reports/defects',
          title: 'Defects',
          querySupport: QuerySupport.BUILDER_AND_PROJECT,
        },
        {
          id: 'adminReportsProjectHoursBudget' as const,
          buildPassModule: BuildPassModule.HoursBudgetReport,
          path: '/admin/reports/project-hours-budget',
          title: 'Project hours budget',
          querySupport: QuerySupport.BUILDER_AND_PROJECT,
        },
      ],
    },
    {
      id: 'workflows' as const,
      buildPassModule: BuildPassModule.Workflows,
      requiredRoles: [Role.BuilderManager, Role.BuilderOwner, Role.SuperAdmin],
      title: 'Workflows',
      icon: IconSubtask,
      items: [
        {
          id: 'workflowTemplates' as const,
          title: 'Configure workflows',
          path: '/workflows',
          actions: {
            list: () => '/workflows',
            new: () => '/workflows/new',
            edit: (id: string) => `/workflows/${id}`,
          } as const,
        },
        {
          id: 'workflowRuns' as const,
          title: 'Workflow runs',
          path: '/workflows/runs',
          actions: {
            list: () => '/workflows/runs',
            view: (id: string) => `/workflows/runs/${id}`,
          } as const,
        },
      ],
    },
  ],
}

const DIRECTORY_MODULES = {
  id: 'directory',
  title: 'Directory',
  items: [
    {
      id: 'subcontractors' as const,
      buildPassModule: BuildPassModule.Subcontractors,
      title: 'Companies',
      path: '/subcontractors',
      icon: IconBriefcase,
      querySupport: {
        '/subcontractors/[subcontractorId]': QuerySupport.BUILDER_AND_PROJECT,
        default: QuerySupport.BUILDER_AND_PROJECT,
      },
      actions: {
        list: (params?: Params.SubbieList) =>
          stringifyUrl('/subcontractors', params),
        bulkUpload: () => '/subcontractors/bulk-upload',
        edit: (subcontractorId: string, params?: Record<string, unknown>) =>
          `/subcontractors/${subcontractorId}/edit${
            params ? buildQueryStringList(params) : ''
          }`,
        view: (
          subcontractorId: string,
          options?: RouteViewOptions<keyof typeof SubbieTabs>
        ) => {
          return `/subcontractors/${subcontractorId}${
            options?.tab ? `?filter=${paramify({ tab: options?.tab })}` : ''
          }`
        },
        newContact: (subcontractorId: string) =>
          `/subcontractors/${subcontractorId}/contacts/new`,
        editContact: (subcontractorId: string, contactId: string) =>
          `/subcontractors/${subcontractorId}/contacts/${contactId}/edit`,
        prequalification: (
          subcontractorId: string,
          prequalificationId: string
        ) =>
          `/subcontractors/${subcontractorId}/prequalifications/${prequalificationId}`,
      } as const,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'users' as const,
      title: 'People',
      icon: IconUsers,
      items: [
        {
          id: 'workers' as const,
          buildPassModule: BuildPassModule.Workers,
          title: 'Workers',
          path: '/workers',
          querySupport: QuerySupport.BUILDER_AND_PROJECT,
          help: 'Any worker that has been inducted or has signed on will be shown here, filtered by the selected project',
          actions: {
            list: (params?: Params.WorkerList) =>
              stringifyUrl('/workers', params),
            new: () => '/workers/new',
            view: (
              workerId: string,
              options?: RouteViewOptions<keyof typeof WorkerTabs>
            ) => {
              return `/workers/${workerId}${
                options?.tab ? `?filter=${paramify({ tab: options?.tab })}` : ''
              }`
            },
            edit: (workerId: string) => `/workers/${workerId}/edit`,
            newTicket: (workerId: string) => `/workers/${workerId}/tickets/new`,
            newDocument: (workerId: string) =>
              `/workers/${workerId}/documents/new`,
            viewDocument: (workerId: string, documentId: string) =>
              `/workers/${workerId}/documents/${documentId}`,
            editTicket: (workerId: string, ticketId: string) =>
              `/workers/${workerId}/tickets/${ticketId}/edit`,
          } as const,
          requiredRoles: BASIC_ADMIN_ROLES,
        },
        {
          id: 'staff' as const,
          buildPassModule: BuildPassModule.Staff,
          title: 'Staff',
          path: '/staff',
          querySupport: QuerySupport.BUILDER_ONLY,
          requiredRoles: BASIC_ADMIN_ROLES,
        },
      ],
    },
  ],
}

export const PROJECT_MANAGEMENT_MODULES = {
  id: 'projectManagement',
  title: 'Project management',
  items: [
    {
      id: 'scheduling' as const,
      buildPassModule: BuildPassModule.Scheduling,
      title: 'Scheduling',
      path: '/scheduling',
      icon: IconCalendar,
      requiredRoles: BASIC_ADMIN_ROLES,
      querySupport: QuerySupport.PROJECT_ONLY,
      actions: {
        list: () => '/scheduling',
        new: () => '/scheduling/new',
        view: (projectScheduleId: string) =>
          config.schedulingUrl + projectScheduleId,
        begin: (projectScheduleId: string) =>
          config.schedulingUrl + projectScheduleId + '/begin',
      } as const,
    },
    {
      id: 'drawings' as const,
      buildPassModule: BuildPassModule.Drawings,
      title: 'Drawings',
      path: '/drawings',
      icon: IconPencil,
      requiredRoles: BASIC_ADMIN_ROLES,
      querySupport: QuerySupport.PROJECT_ONLY,
      actions: {
        view: (drawingId: string) => `/drawings/${drawingId}`,
        list: (folderId?: string) =>
          `/drawings${folderId ? `?folderId=${folderId}` : ''}`,
      } as const,
    },
    {
      id: 'documents' as const,
      buildPassModule: BuildPassModule.Documents,
      title: 'Documents',
      icon: IconFolder,
      requiredRoles: BASIC_ADMIN_ROLES,
      items: [
        {
          id: 'documentsProject' as const,
          title: 'Project documents',
          path: '/documents/project',
          help: 'Project-specific documents optionally made available to workers',
          querySupport: QuerySupport.PROJECT_ONLY,
          actions: {
            list: (options?: FilterObjectType, folderId?: string) =>
              updateFiltersIfPresent(
                setFolderIdQueryParam('/documents/project', folderId),
                options
              ),
            new: (folderId?: string) =>
              setFolderIdQueryParam('/documents/project/new', folderId),
            view: (projectDocId: string) =>
              `/documents/project/${projectDocId}`,
          } as const,
        },
        {
          id: 'documentsCompany' as const,
          title: 'Company documents',
          path: '/documents/company',
          help: 'Company-wide documents available across all projects',
          querySupport: QuerySupport.BUILDER_ONLY,
          actions: {
            list: (options?: FilterObjectType, folderId?: string) =>
              updateFiltersIfPresent(
                setFolderIdQueryParam('/documents/company', folderId),
                options
              ),
            new: (folderId?: string) =>
              setFolderIdQueryParam('/documents/company/new', folderId),
            view: (companyDocId: string) =>
              `/documents/company/${companyDocId}`,
          } as const,
        },
        {
          id: 'documentsOther' as const,
          title: 'Other documents',
          path: '/documents/other',
          help: 'All extra documents submitted by subcontractors',
          querySupport: {
            '/documents/other/[documentId]': QuerySupport.BUILDER_AND_PROJECT,
            default: QuerySupport.PROJECT_ONLY,
          },
          actions: {
            list: (options?: FilterObjectType, folderId?: string) =>
              updateFiltersIfPresent(
                setFolderIdQueryParam('/documents/other', folderId),
                options
              ),
            new: (folderId?: string) =>
              setFolderIdQueryParam('/documents/other/new', folderId),
            view: (docId: string) => `/documents/other/${docId}`,
          } as const,
        },
      ],
    },
    {
      id: 'meetings',
      title: 'Meetings',
      icon: IconCalendar,
      items: [
        {
          id: 'toolboxes' as const,
          buildPassModule: BuildPassModule.Toolboxes,
          title: 'Toolboxes',
          path: '/toolboxes',
          querySupport: QuerySupport.PROJECT_ONLY,
          actions: {
            new: () => '/toolboxes/new',
            edit: (toolboxId: string) => `/toolboxes/${toolboxId}/edit`,
            list: (options?: FilterObjectType) =>
              updateFiltersIfPresent('/toolboxes', options),
            run: (toolboxId: string) => `/toolboxes/${toolboxId}/run`,
            view: (
              toolboxId: string,
              options?: RouteViewOptions<keyof typeof TOOLBOX_TABS>,
              openInviteModal?: boolean
            ) => {
              const filterParams = options?.tab
                ? `filter=${paramify({ tab: TOOLBOX_TABS[options?.tab] })}`
                : ''
              const inviteModalParam = openInviteModal
                ? `showInviteModal=true`
                : ''

              const queryParams = [filterParams, inviteModalParam]
                .filter(Boolean)
                .join('&')

              return `/toolboxes/${toolboxId}${queryParams ? `?${queryParams}` : ''}`
            },
          } as const,
          requiredRoles: BASIC_ADMIN_ROLES,
        },
      ],
    },

    {
      id: 'projectMap' as const,
      buildPassModule: BuildPassModule.ProjectMap,
      path: '/dashboards/map',
      title: 'Map',
      icon: IconMap2,
      querySupport: QuerySupport.BUILDER_AND_PROJECT,
      requiredRoles: [Role.BuilderManager, Role.BuilderOwner, Role.SuperAdmin],
    },
  ],
}

export const SITE_MANAGEMENT_MODULES = {
  id: 'siteManagement',
  title: 'Site management',
  items: [
    {
      id: 'photos' as const,
      buildPassModule: BuildPassModule.ImageGallery,
      path: '/dashboards/gallery',
      title: 'Photos',
      icon: IconPhoto,
      querySupport: QuerySupport.PROJECT_ONLY,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'checklists' as const,
      buildPassModule: BuildPassModule.Checklists,
      title: 'Forms',
      icon: IconListCheck,
      items: CHECKLIST_ITEMS,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'actions' as const,
      buildPassModule: BuildPassModule.Actions,
      title: 'Actions',
      path: '/actions',
      icon: IconBolt,
      querySupport: QuerySupport.BUILDER_AND_PROJECT,
      actions: {
        new: () => '/actions/new',
        edit: (id: string) => `/actions/${id}/edit`,
        list: (params?: Params.ActionsList) => stringifyUrl(`/actions`, params),
      } as const,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'siteDiaries' as const,
      buildPassModule: BuildPassModule.SiteDiaries,
      title: 'siteDiary:plural',
      path: '/site-diary',
      icon: IconPencil,
      querySupport: QuerySupport.PROJECT_ONLY,
      actions: {
        new: (duplicateSiteDiaryId?: string) =>
          `/site-diary/new${
            duplicateSiteDiaryId ? `?duplicate=${duplicateSiteDiaryId}` : ''
          }`,
        edit: (siteDiaryId: string) => `/site-diary/${siteDiaryId}/edit`,
        list: (params?: Params.SiteDiaryList) =>
          stringifyUrl('/site-diary', params),
        view: (siteDiaryId: string) => `/site-diary/${siteDiaryId}`,
        aiSummary: () => `/site-diary/weekly-ai-report`,
      } as const,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'preStarts' as const,
      title: 'Notices',
      icon: IconSpeakerphone,
      path: '/safety/pre-starts',
      buildPassModule: BuildPassModule.PreStarts,
      querySupport: QuerySupport.PROJECT_ONLY,
      actions: {
        new: () => '/safety/pre-starts/new',
        list: (options?: FilterObjectType) =>
          updateFiltersIfPresent('/safety/pre-starts', options),
        edit: (preStartId: string) => `/safety/pre-starts/${preStartId}/edit`,
        view: (preStartId: string) => {
          return `/safety/pre-starts/${preStartId}`
        },
      } as const,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'communication' as const,
      buildPassModule: BuildPassModule.Communication,
      title: 'Messaging',
      icon: IconMessageCircle,
      path: '/communication',
      querySupport: QuerySupport.BUILDER_AND_PROJECT,
      actions: {
        new: (params?: Record<string, unknown>) =>
          `/communication/new${buildQueryStringList(params)}`,
        newAllProjects: () => `/communication/new-all-projects`,
        list: (options?: FilterObjectType) =>
          updateFiltersIfPresent('/communication', options),
        view: (toolboxId: string) => `/communication/${toolboxId}`,
      } as const,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'timesheets' as const,
      buildPassModule: BuildPassModule.Timesheets,
      title: 'Timesheets',
      path: '/timesheets',
      icon: IconClockEdit,
      querySupport: QuerySupport.BUILDER_AND_PROJECT,
      actions: {
        list: () => '/timesheets',
      },
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'locations' as const,
      title: 'Locations',
      icon: IconDoor,
      path: '/locations',
      querySupport: QuerySupport.PROJECT_ONLY,
      buildPassModule: BuildPassModule.Locations,
      actions: {
        list: () => '/locations',
        view: (id: string) => `/locations/${id}`,
      } as const,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
  ],
}

export const SAFETY_MODULES = {
  id: 'safety',
  title: 'Safety',
  icon: IconShieldCheck,
  path: '/safety',
  items: [
    {
      id: 'signOns' as const,
      title: 'Sign ons',
      icon: IconClockHour3,
      path: '/safety/sign-ons',
      querySupport: {
        '/safety/sign-ons/[signOnId]': QuerySupport.BUILDER_AND_PROJECT,
        default: QuerySupport.BUILDER_AND_PROJECT,
      },
      actions: {
        new: () => '/safety/sign-ons/new',
        list: (params?: Params.SignOnList) =>
          stringifyUrl('/safety/sign-ons', params),
        view: (signOnId: string) => {
          return `/safety/sign-ons/${signOnId}`
        },
      } as const,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'inductions' as const,
      title: 'induction:plural',
      icon: IconCheckbox,
      items: [
        {
          id: 'siteInductions' as const,
          title: 'induction:siteInductions',
          path: '/safety/inductions/project',
          help: `siteInduction:messages.siteOneTimeInduction`,
          querySupport: {
            '/safety/inductions/project/[inductionId]':
              QuerySupport.BUILDER_AND_PROJECT,
            default: QuerySupport.PROJECT_ONLY,
          },
          actions: {
            new: () => '/safety/inductions/project/new',
            list: (params?: Params.SiteInductionsList) =>
              stringifyUrl(`/safety/inductions/project`, params),
            view: (inductionId: string) => {
              return `/safety/inductions/project/${inductionId}`
            },
            viewByIdentifier: (projectId: string, identifier: number) =>
              `/safety/inductions/project/by-identifier/${projectId}/${identifier.toString()}`,
            listTemplates: () => `/safety/inductions/project/templates/list`,
            editTemplate: (templateId: string) => {
              return `/safety/inductions/project/templates/${templateId}`
            },
          } as const,
          requiredRoles: BASIC_ADMIN_ROLES,
        },
        {
          id: 'companyInductions' as const,
          title: 'induction:companyInductions',
          path: '/safety/inductions/builder',
          help: `induction:tooltips.companyOneTimeInduction`,
          querySupport: QuerySupport.BUILDER_AND_PROJECT,
          actions: {
            new: () => '/safety/inductions/builder/new',
            list: (params?: Params.CompanyInductionsList) =>
              stringifyUrl(`/safety/inductions/builder`, params),
            view: (inductionId: string) => {
              return `/safety/inductions/builder/${inductionId}`
            },
          } as const,
          requiredRoles: BASIC_ADMIN_ROLES,
        },
      ],
    },
    {
      id: 'swms' as const,
      title: 'swms:plural',
      icon: IconFileText,
      items: [
        {
          id: 'documentsSwms' as const,
          title: 'View all',
          path: '/documents/swms',
          querySupport: {
            '/documents/swms/[swmsId]': QuerySupport.BUILDER_AND_PROJECT,
            default: QuerySupport.PROJECT_ONLY,
          },
          actions: {
            new: () => '/documents/swms/new',
            list: (params?: Params.SwmsList) =>
              stringifyUrl(`/documents/swms`, params),
            signOn: (swmsDocId: string) =>
              `/documents/swms/${swmsDocId}/sign-on`,
            view: (swmsDocId: string) => {
              return `/documents/swms/${swmsDocId}`
            },
          } as const,
          requiredRoles: BASIC_ADMIN_ROLES,
        },
        {
          id: 'swms-builder' as const,
          title: 'swmsBuilder:title',
          path: '/swms-builder',
          querySupport: QuerySupport.PROJECT_ONLY,
          buildPassModule: BuildPassModule.SwmsBuilder,
          actions: {
            view: () => `/swms-builder`,
          },
          requiredRoles: BASIC_ADMIN_ROLES,
        },
      ],
    },
    {
      id: 'documentsMsds' as const,
      querySupport: QuerySupport.BUILDER_AND_PROJECT,
      title: 'SDS',
      icon: IconNews,
      path: '/documents/msds',
      buildPassModule: BuildPassModule.Sds,
      actions: {
        new: () => '/documents/msds/new',
        list: (params?: Params.SdsList) =>
          stringifyUrl('/documents/msds', params),
        view: (msdsDocId: string) => {
          return `/documents/msds/${msdsDocId}`
        },
      } as const,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'emergency' as const,
      buildPassModule: BuildPassModule.Emergency,
      title: 'Emergency',
      icon: IconAlertTriangle,
      path: '/emergency',
      querySupport: QuerySupport.PROJECT_ONLY,
      actions: {
        new: () => '/emergency/new',
        list: () => '/emergency',
        view: (emergencyId: string) => `/emergency/${emergencyId}`,
        edit: (emergencyId: string) => `/emergency/${emergencyId}/edit`,
      } as const,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'permits' as const,
      buildPassModule: BuildPassModule.Permits,
      title: 'Permits',
      path: '/permits',
      icon: IconParkingCircle,
      querySupport: QuerySupport.PROJECT_ONLY,
      actions: {
        list: (params?: Params.PermitList) => stringifyUrl('/permits', params),
        new: () => '/permits/new',
        edit: (permitId: string) => `/permits/${permitId}/edit`,
        editWorker: (permitId: string, permitWorkerId: string) =>
          `/permits/${permitId}/${permitWorkerId}/edit`,
        listTypes: (createDrawerOpen?: boolean) => {
          return stringifyUrl(
            `/permits/types`,
            createDrawerOpen ? { createDrawerOpen } : {}
          )
        },
        editType: (permitTypeId: string) =>
          `/permits/types/${permitTypeId}/edit`,
        editTypeTemplate: (permitTypeId: string, templateId: string) =>
          `/permits/types/${permitTypeId}/${templateId}`,
      },
      requiredRoles: BASIC_ADMIN_ROLES,
    },
  ],
}

export const COMPLIANCE_MODULES = {
  id: 'compliance',
  title: 'Compliance',
  items: [
    {
      id: 'documentsInsurances' as const,
      title: 'Insurances',
      icon: IconClipboardText,
      path: '/documents/insurances',
      querySupport: QuerySupport.BUILDER_AND_PROJECT,
      actions: {
        new: () => '/documents/insurances/new',
        list: (options?: FilterObjectType) =>
          updateFiltersIfPresent('/documents/insurances', options),
        view: (insuranceDocId: string) => {
          return `/documents/insurances/${insuranceDocId}`
        },
      } as const,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'prequalifications' as const,
      querySupport: QuerySupport.BUILDER_ONLY,
      title: 'Pre-qualifications',
      path: '/prequalifications',
      icon: IconFileCheck,
      actions: {
        new: () => '/prequalifications/new',
        list: (options?: FilterObjectType) =>
          updateFiltersIfPresent('/prequalifications', options),
        request: () => '/prequalifications/request',
        edit: (prequalId: string) => `/prequalifications/${prequalId}/edit`,
        view: (prequalId: string) => {
          return `/prequalifications/${prequalId}`
        },
      } as const,
      buildPassModule: BuildPassModule.Prequalifications,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'tickets' as const,
      buildPassModule: BuildPassModule.Tickets,
      title: 'ticket:plural',
      path: '/tickets',
      icon: IconId,
      querySupport: QuerySupport.BUILDER_ONLY,
      actions: {
        list: (params?: Params.TicketList) => stringifyUrl('/tickets', params),
      },
      requiredRoles: BASIC_ADMIN_ROLES,
    },
  ],
}

export const QUALITY_ASSURANCE_MODULES = {
  id: 'qualityAssurance',
  title: 'Quality assurance',
  items: [
    {
      id: 'itps' as const,
      buildPassModule: BuildPassModule.Itps,
      title: 'Inspections',
      icon: IconReportSearch,
      items: [
        {
          id: 'itpsInspections' as const,
          querySupport: QuerySupport.BUILDER_AND_PROJECT,
          title: 'View all',
          path: '/itps',
          actions: {
            newITP: (params?: Record<string, unknown>) =>
              `/itps/new${params ? buildQueryStringList(params) : ''}`,

            ITPTemplateTypeList: () => '/itps',
            ITPSOfTemplateType: (templateId: string) => `/itps/${templateId}`,

            viewITP: (templateId: string, templateResponseId: string) =>
              `/itps/${templateId}/${templateResponseId}`,
            viewReport: (templateId: string, templateResponseId: string) => {
              return `/itps/${templateId}/${templateResponseId}/report`
            },
          } as const,
        },
        {
          id: 'itpsTemplates' as const,
          querySupport: QuerySupport.BUILDER_AND_PROJECT,
          title: 'Templates',
          path: '/itps/templates',
          requiredRoles: [
            Role.BuilderManager,
            Role.BuilderOwner,
            Role.SuperAdmin,
          ],
          actions: {
            list: (options?: FilterObjectType) =>
              updateFiltersIfPresent('/itps/templates', options),
            view: (templateId: string) => `/itps/templates/${templateId}`,
          } as const,
        },
      ],
      requiredRoles: BASIC_ADMIN_ROLES,
    },
    {
      id: 'defects' as const,
      buildPassModule: BuildPassModule.Defects,
      title: 'Defects',
      path: '/defects',
      icon: IconExclamationCircle,
      querySupport: {
        '/defects/[defectId]': QuerySupport.BUILDER_AND_PROJECT,
        default: QuerySupport.PROJECT_ONLY,
      },
      help: 'Create & assign defects on the fly, then send messages back and forth to get them fixed with ease',
      actions: {
        list: (params?: Params.DefectList) => stringifyUrl('/defects', params),
        pending: () => '/defects/pending',
        view: (id: string) => `/defects/${id}`,
      },
      requiredRoles: BASIC_ADMIN_ROLES,
    },
  ],
}

export const ASSET_MANAGEMENT_MODULES = {
  id: 'assetManagement',
  title: 'Asset management',
  items: [
    {
      id: 'registers' as const,
      buildPassModule: BuildPassModule.Registers,
      title: 'Registers',
      path: '/registers',
      icon: IconRegistered,
      querySupport: QuerySupport.BUILDER_ONLY,
      actions: {
        list: (options?: FilterObjectType) =>
          updateFiltersIfPresent('/registers', options),
        listItemTypes: (createDrawerOpen?: boolean) => {
          return stringifyUrl(
            `/registers/item-types`,
            createDrawerOpen ? { createDrawerOpen } : {}
          )
        },
        new: () => '/registers/new',
        editRegister: (registerId: string) => `/registers/${registerId}/edit`,
        viewRegister: (registerId: string) => `/registers/${registerId}`,
        newRegisterItem: (registerId: string) => `/registers/${registerId}/new`,
        viewRegisterItem: (
          registerId: string,
          registerItemId: string | number
        ) => `/registers/${registerId}/${registerItemId}`,
        viewRegisterTemplate: (
          registerId: string,
          templateId: string | number
        ) => `/registers/${registerId}/template/${templateId}`,
        editRegisterItemType: (registerItemTypeId: string) =>
          `/registers/item-types/${registerItemTypeId}/edit`,
        editRegisterItemTypeTemplate: (
          registerItemTypeId: string,
          templateId: string
        ) => `/registers/item-types/${registerItemTypeId}/${templateId}`,
      } as const,
      requiredRoles: BASIC_ADMIN_ROLES,
    },
  ],
}

const SUPER_ADMIN_MODULES = {
  id: 'superAdmin' as const,
  title: 'Super Admin',
  requiredRoles: [Role.SuperAdmin, Role.BpSales],
  items: [
    {
      id: 'support' as const,
      title: 'BuildPass support',
      icon: IconLifebuoy,
      items: [
        {
          id: 'superResource' as const,
          title: 'Resource',
          icon: IconHomeSearch,
          requiredRoles: [Role.SuperAdmin],
          items: [
            {
              id: 'superResourceSearch' as const,
              path: '/super/resource/search',
              title: 'Resource search',
              querySupport: QuerySupport.BUILDER_ONLY,
            },
            {
              id: 'superSignOnMap' as const,
              path: '/super/resource/sign-on-map',
              title: 'Sign on map',
              querySupport: QuerySupport.BUILDER_ONLY,
            },
          ],
        },
        {
          id: 'superBuilders' as const,
          title: 'Builders',
          icon: IconBuildingCommunity,
          requiredRoles: [Role.SuperAdmin, Role.BpSales],
          items: [
            {
              id: 'superBuildersList' as const,
              path: '/super/builders',
              title: 'Builders list',
              querySupport: QuerySupport.BUILDER_ONLY,
              requiredRoles: [Role.SuperAdmin, Role.BpSales],
              actions: {
                list: () => '/super/builders',
                view: (id: string) => `/super/builders/${id}`,
                edit: (id: string) => `/super/builders/${id}/edit`,
                new: () => '/super/builders/new',
              },
            },
            {
              id: 'superBuilderModules' as const,
              path: '/super/modules',
              title: 'Configure modules',
              requiredRoles: [Role.SuperAdmin],
            },
          ],
        },
        {
          id: 'superDashboards' as const,
          title: 'Dashboards',
          icon: IconLayoutBoard,
          requiredRoles: [Role.SuperAdmin],
          items: [
            {
              id: 'superDashboards' as const,
              path: '/super/dashboards',
              title: 'Global',
            },
          ],
        },
        {
          id: 'superTemplates' as const,
          title: 'Templates',
          icon: IconTemplate,
          requiredRoles: [Role.SuperAdmin],
          items: [
            {
              id: 'superTemplatesCompany' as const,
              path: '/super/templates/company',
              title: 'Company templates',
              actions: {
                list: () => '/super/templates/company',
                view: (id: string) => `/super/templates/company/${id}`,
                new: () => '/super/templates/company/new',
              },
            },
            {
              id: 'superTemplatesLibrary' as const,
              path: '/super/templates/library',
              title: 'Library templates',
              actions: {
                list: () => '/super/templates/library',
                view: (id: string) => `/super/templates/library/${id}`,
                new: () => '/super/templates/library/new',
              },
            },
            {
              id: 'superTicketTemplates' as const,
              path: '/super/ticket-templates',
              title: 'Ticket templates',
              actions: {
                new: () => '/super/ticket-templates/new',
                list: (options?: FilterObjectType) =>
                  updateFiltersIfPresent('/super/ticket-templates', options),
              },
            },
          ],
        },
        {
          id: 'superDirectory' as const,
          title: 'Global directory ',
          icon: IconWorld,
          requiredRoles: [Role.SuperAdmin],
          items: [
            {
              id: 'superAdminUser' as const,
              path: '/super/admin-users',
              title: 'Admin user search',
            },
            {
              id: 'superWorker' as const,
              path: '/super/workers',
              title: 'Worker actions',
            },
            {
              id: 'superCompanies' as const,
              path: '/super/companies',
              title: 'Companies',
            },
            {
              id: 'externalLinks' as const,
              path: '/super/external-links',
              title: 'External links',
            },
          ],
        },
      ],
    },
  ],
}

export const NAVIGATION_CONFIG = [
  GENERAL_MODULES,
  DIRECTORY_MODULES,
  PROJECT_MANAGEMENT_MODULES,
  SITE_MANAGEMENT_MODULES,
  SAFETY_MODULES,
  COMPLIANCE_MODULES,
  QUALITY_ASSURANCE_MODULES,
  ASSET_MANAGEMENT_MODULES,
  SUPER_ADMIN_MODULES,
]
